import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import LearnerPortalShell from "@/components/learner/LearnerPortalShell";
import {
  getPerStateCdl,
  type CdlClass,
  type CdlKnowledgeTest,
  type CdlSkillsTestComponent,
  type LocalizedString,
  type PerStateCdl,
} from "@/data/per-state-cdl";

/**
 * apps/web :: pages/cdl :: CdlLicensePage
 *
 * The `/cdl/license/:state` route — Pillar B per-state CDL info page.
 *
 * Wave 12 scope:
 *   - Texas is the first state authored. `getPerStateCdl('TX')` returns
 *     full content (FMCSA + TX classes A/B/C).
 *   - For any other state code, render a "your state is coming soon"
 *     stub with a CTA back to Texas (the first-state preview).
 *
 * Localization (Wave 12 first cut):
 *   - The wrapping UI strings (titles, headings, CTAs) come from
 *     `react-i18next` and exist in all 6 GA locales.
 *   - The per-state CDL data block is currently EN+ES only. Wave 13
 *     backfills hi / zh-CN / tl / tr. Until then, locales beyond
 *     en/es fall back to en for the data-block text via the
 *     `LocalizedString` shape's optional locale slots.
 *
 * Status-agnostic (ROADS-436):
 *   - The page never asks immigration / citizenship / residency status.
 *   - Documents are described as categories, never as advice for a
 *     specific population.
 */

type DataLang = keyof LocalizedString;

const SUPPORTED_DATA_LANGS: readonly DataLang[] = [
  "en",
  "es",
  "hi",
  "zh-CN",
  "tl",
  "tr",
] as const;

function pickDataLang(rawLang: string | undefined): DataLang {
  // Match Wave 8 i18n locales: try the full BCP-47 tag (e.g. "zh-CN")
  // then fall back to the primary subtag (e.g. "es" from "es-MX").
  // Anything not in the supported set ends up at "en".
  if (!rawLang) return "en";
  if (SUPPORTED_DATA_LANGS.includes(rawLang as DataLang)) {
    return rawLang as DataLang;
  }
  const primary = rawLang.split("-")[0].toLowerCase();
  if (SUPPORTED_DATA_LANGS.includes(primary as DataLang)) {
    return primary as DataLang;
  }
  return "en";
}

/**
 * Read a LocalizedString slot, preferring the requested locale and
 * falling back to English when that locale is not yet authored. This
 * keeps the page working in hi / zh-CN / tl / tr before Wave 13's
 * data-side translations land.
 */
function localized(s: LocalizedString, lang: DataLang): string {
  const candidate = s[lang];
  if (typeof candidate === "string" && candidate.length > 0) return candidate;
  return s.en;
}

export default function CdlLicensePage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const params = useParams<{ state?: string }>();
  const requestedState = (params.state ?? "").toUpperCase();
  const content = useMemo(
    () => (requestedState ? getPerStateCdl(requestedState) : undefined),
    [requestedState],
  );
  const lang = pickDataLang(i18n.language);

  if (!requestedState) {
    return (
      <LearnerPortalShell pageTitle={t("cdl.license.title", { state: "" })}>
        <div
          role="alert"
          data-testid="cdl-license-no-state"
          className="mx-auto mt-xl max-w-2xl px-md py-lg text-center text-neutral-700"
        >
          {t("cdl.license.noStateBody")}
        </div>
      </LearnerPortalShell>
    );
  }

  if (!content) {
    return (
      <LearnerPortalShell
        pageTitle={t("cdl.license.notReady.title", { code: requestedState })}
      >
        <article
          data-testid="cdl-license-not-ready"
          className="mx-auto max-w-2xl px-md py-lg sm:px-lg"
        >
          <h1 className="font-display text-2xl font-bold text-brand-primary sm:text-3xl">
            {t("cdl.license.notReady.title", { code: requestedState })}
          </h1>
          <p className="mt-md text-neutral-700">
            {t("cdl.license.notReady.body", { code: requestedState })}
          </p>
          <Link
            to="/cdl/license/TX"
            data-testid="cdl-license-not-ready-tx-cta"
            className="mt-lg inline-flex items-center gap-2 rounded-md bg-brand-primary px-md py-sm text-sm font-semibold text-white no-underline hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
          >
            {t("cdl.license.notReady.ctaTexas")}
          </Link>
        </article>
      </LearnerPortalShell>
    );
  }

  return (
    <LearnerPortalShell
      pageTitle={t("cdl.license.title", {
        state: localized(content.displayName, lang),
      })}
    >
      <CdlContent content={content} lang={lang} />
    </LearnerPortalShell>
  );
}

interface CdlContentProps {
  content: PerStateCdl;
  lang: DataLang;
}

function CdlContent({ content, lang }: CdlContentProps): JSX.Element {
  const { t } = useTranslation();
  const stateName = localized(content.displayName, lang);

  return (
    <article
      data-testid={`cdl-license-${content.stateCode.toLowerCase()}`}
      className="mx-auto max-w-4xl px-md py-lg sm:px-lg sm:py-xl"
    >
      <header>
        <h1 className="font-display text-3xl font-bold tracking-tight text-brand-primary sm:text-4xl">
          {t("cdl.license.title", { state: stateName })}
        </h1>
      </header>

      {/* ── Classes ─────────────────────────────────────────────── */}
      <section
        aria-labelledby="cdl-classes-heading"
        className="mt-xl rounded-lg border border-neutral-200 bg-white p-md sm:p-lg"
        data-testid="cdl-classes"
      >
        <h2
          id="cdl-classes-heading"
          className="font-display text-xl font-bold text-brand-primary"
        >
          {t("cdl.license.classes.heading")}
        </h2>
        <p className="mt-sm text-neutral-700">
          {t("cdl.license.classes.intro", { state: stateName })}
        </p>
        <div className="mt-md grid gap-md sm:grid-cols-1 lg:grid-cols-3">
          {content.classes.map((c) => (
            <ClassCard
              key={c.code}
              cls={c}
              lang={lang}
              stateName={stateName}
            />
          ))}
        </div>
      </section>

      {/* ── Fees ────────────────────────────────────────────────── */}
      <section
        aria-labelledby="cdl-fees-heading"
        className="mt-lg rounded-lg border border-neutral-200 bg-white p-md sm:p-lg"
        data-testid="cdl-fees"
      >
        <h2
          id="cdl-fees-heading"
          className="font-display text-xl font-bold text-brand-primary"
        >
          {t("cdl.license.fees.heading")}
        </h2>
        <dl className="mt-sm grid gap-sm sm:grid-cols-2">
          <div>
            <dt className="text-sm text-neutral-600">
              {t("cdl.license.fees.cdlOriginal")}
            </dt>
            <dd className="text-lg font-semibold text-slate-900">
              {t("cdl.license.fees.amount", {
                amount: content.apply.fees.cdlOriginalUsd,
              })}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-neutral-600">
              {t("cdl.license.fees.clp")}
            </dt>
            <dd className="text-lg font-semibold text-slate-900">
              {t("cdl.license.fees.amount", {
                amount: content.apply.fees.commercialLearnerPermitUsd,
              })}
            </dd>
          </div>
        </dl>
        <p className="mt-md text-sm text-neutral-700">
          {localized(content.apply.fees.notes, lang)}
        </p>
      </section>

      {/* ── Documents ────────────────────────────────────────────── */}
      <section
        aria-labelledby="cdl-docs-heading"
        className="mt-lg rounded-lg border border-neutral-200 bg-white p-md sm:p-lg"
        data-testid="cdl-documents"
      >
        <h2
          id="cdl-docs-heading"
          className="font-display text-xl font-bold text-brand-primary"
        >
          {t("cdl.license.documents.heading")}
        </h2>
        {content.apply.documentsCrossReferenceClassC ? (
          <>
            <p className="mt-sm text-neutral-700">
              {t("cdl.license.documents.crossRefClassC", { state: stateName })}
            </p>
            <Link
              to={`/apply/${content.stateCode}`}
              data-testid="cdl-documents-classc-link"
              className="mt-md inline-block text-sm font-semibold text-brand-primary underline underline-offset-2 hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
            >
              {t("cdl.license.documents.openClassC", { state: stateName })}
            </Link>
          </>
        ) : null}
        <ul className="mt-md space-y-md">
          {content.apply.cdlSpecificDocuments.map((doc) => (
            <li key={doc.id} className="border-l-4 border-brand-primary/40 pl-md">
              <h3 className="text-base font-semibold text-slate-900">
                {localized(doc.label, lang)}
              </h3>
              <p className="mt-xs text-sm text-neutral-700">
                {localized(doc.detail, lang)}
              </p>
            </li>
          ))}
        </ul>
      </section>

      {/* ── Knowledge tests ──────────────────────────────────────── */}
      <section
        aria-labelledby="cdl-knowledge-heading"
        className="mt-lg rounded-lg border border-neutral-200 bg-white p-md sm:p-lg"
        data-testid="cdl-knowledge-tests"
      >
        <h2
          id="cdl-knowledge-heading"
          className="font-display text-xl font-bold text-brand-primary"
        >
          {t("cdl.license.knowledgeTests.heading")}
        </h2>
        <p className="mt-sm text-neutral-700">
          {t("cdl.license.knowledgeTests.intro", { state: stateName })}
        </p>
        <ol className="mt-md list-decimal space-y-sm pl-6">
          {content.knowledgeTests.map((kt) => (
            <KnowledgeTestRow key={kt.id} test={kt} lang={lang} />
          ))}
        </ol>
      </section>

      {/* ── Skills test ──────────────────────────────────────────── */}
      <section
        aria-labelledby="cdl-skills-heading"
        className="mt-lg rounded-lg border border-neutral-200 bg-white p-md sm:p-lg"
        data-testid="cdl-skills-test"
      >
        <h2
          id="cdl-skills-heading"
          className="font-display text-xl font-bold text-brand-primary"
        >
          {t("cdl.license.skills.heading")}
        </h2>
        <p className="mt-sm text-neutral-700">
          {localized(content.skillsTest.overview, lang)}
        </p>
        <ul className="mt-md space-y-sm">
          {content.skillsTest.components.map((c: CdlSkillsTestComponent) => (
            <li key={c.id} className="rounded border border-neutral-200 p-sm">
              <p className="text-sm font-semibold text-slate-900">
                {localized(c.label, lang)}
              </p>
              <p className="mt-xs text-sm text-neutral-700">
                {localized(c.detail, lang)}
              </p>
            </li>
          ))}
        </ul>
        {content.skillsTest.thirdPartyAvailable ? (
          <p className="mt-md text-sm text-neutral-600">
            {t("cdl.license.skills.thirdPartyAvailable")}
          </p>
        ) : null}
      </section>

      {/* ── Office finder ────────────────────────────────────────── */}
      <section
        aria-labelledby="cdl-office-heading"
        className="mt-lg rounded-lg border border-neutral-200 bg-white p-md sm:p-lg"
        data-testid="cdl-office-finder"
      >
        <h2
          id="cdl-office-heading"
          className="font-display text-xl font-bold text-brand-primary"
        >
          {t("cdl.license.officeFinder.heading", { state: stateName })}
        </h2>
        <p className="mt-sm text-neutral-700">
          {t("cdl.license.officeFinder.body", { state: stateName })}
        </p>
        <a
          href={content.apply.officeFinderUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="cdl-office-finder-link"
          className="mt-md inline-flex items-center gap-2 rounded-md bg-brand-primary px-md py-sm text-sm font-semibold text-white no-underline hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          {t("cdl.license.officeFinder.cta", { state: stateName })}
          <span className="sr-only">
            {t("cdl.license.officeFinder.openInNewTab")}
          </span>
        </a>
      </section>

      {/* ── Handbook ─────────────────────────────────────────────── */}
      <section
        aria-labelledby="cdl-handbook-heading"
        className="mt-lg rounded-lg border border-neutral-200 bg-white p-md sm:p-lg"
        data-testid="cdl-handbook"
      >
        <h2
          id="cdl-handbook-heading"
          className="font-display text-xl font-bold text-brand-primary"
        >
          {t("cdl.license.handbook.heading")}
        </h2>
        <ul className="mt-sm space-y-xs text-sm">
          <li>
            <a
              href={content.handbookEnUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cdl-handbook-en"
              className="text-brand-primary underline underline-offset-2"
            >
              {t("cdl.license.handbook.en", { state: stateName })}
            </a>
          </li>
          <li>
            <a
              href={content.handbookEsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cdl-handbook-es"
              className="text-brand-primary underline underline-offset-2"
            >
              {t("cdl.license.handbook.es", { state: stateName })}
            </a>
          </li>
        </ul>
      </section>

      {/* ── Unlock CTA ───────────────────────────────────────────── */}
      <section
        aria-labelledby="cdl-unlock-heading"
        className="mt-xl rounded-lg border border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50 p-md shadow-sm sm:p-lg"
        data-testid="cdl-unlock"
      >
        <h2
          id="cdl-unlock-heading"
          className="font-display text-lg font-bold text-amber-900"
        >
          {t("cdl.license.unlock.heading")}
        </h2>
        <p className="mt-xs text-sm text-amber-800">
          {t("cdl.license.unlock.body", { state: stateName })}
        </p>
        <Link
          to={`/checkout?tier=cdl&state=${content.stateCode}`}
          data-testid="cdl-unlock-cta"
          className="mt-md inline-flex items-center rounded-full bg-amber-600 px-md py-sm text-sm font-semibold text-white no-underline transition-colors hover:bg-amber-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-700"
        >
          {t("cdl.license.unlock.cta", { state: stateName })}
        </Link>
      </section>
    </article>
  );
}

interface ClassCardProps {
  cls: CdlClass;
  lang: DataLang;
  stateName: string;
}

function ClassCard({ cls, lang, stateName }: ClassCardProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      data-testid={`cdl-class-${cls.code}`}
      className="rounded-lg border border-neutral-200 bg-slate-50 p-md"
    >
      <h3 className="text-lg font-bold text-slate-900">
        {localized(cls.label, lang)}
      </h3>
      <p className="mt-xs text-sm text-neutral-700">
        {localized(cls.definition, lang)}
      </p>
      <dl className="mt-sm space-y-xs text-sm">
        <div>
          <dt className="font-semibold text-slate-900">
            {t("cdl.license.classes.exampleVehicles")}
          </dt>
          <dd className="text-neutral-700">
            {localized(cls.exampleVehicles, lang)}
          </dd>
        </div>
        <div>
          <dt className="font-semibold text-slate-900">
            {t("cdl.license.classes.commonOccupations")}
          </dt>
          <dd className="text-neutral-700">
            {localized(cls.commonOccupations, lang)}
          </dd>
        </div>
      </dl>
      <ul className="mt-sm list-disc space-y-xs pl-5 text-sm text-neutral-700">
        <li>
          {t("cdl.license.classes.ageInterstate", { age: cls.minAgeInterstate })}
        </li>
        <li>
          {t("cdl.license.classes.ageIntrastate", {
            age: cls.minAgeIntrastate,
            state: stateName,
          })}
        </li>
      </ul>
    </div>
  );
}

interface KnowledgeTestRowProps {
  test: CdlKnowledgeTest;
  lang: DataLang;
}

function KnowledgeTestRow({ test, lang }: KnowledgeTestRowProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <li
      data-testid={`cdl-knowledge-test-${test.id}`}
      className="text-sm text-neutral-800"
    >
      <p className="font-semibold text-slate-900">{localized(test.label, lang)}</p>
      <p className="mt-xs text-neutral-700">{localized(test.scope, lang)}</p>
      <p className="mt-xs text-xs text-neutral-600">
        {t("cdl.license.knowledgeTests.appliesTo", {
          classes: test.appliesToClasses.join(" / "),
        })}
        {test.conditional ? ` · ${t("cdl.license.knowledgeTests.conditional")}` : ""}
      </p>
    </li>
  );
}
